import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Upload, AlertCircle, CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Building2, Loader2, Hash, Play, UserX, Receipt, Plus, Trash2, FileSpreadsheet, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (n) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

// Tipos de comprobante (TIFA_ID_TIFA)
const TIFA_OPCIONES = [
  { id: 1, label: 'A' },
  { id: 2, label: 'B' },
  { id: 3, label: 'C' },
  { id: 4, label: 'E' },
  { id: 12, label: 'TFA' },
  { id: 11, label: 'TFB' },
  { id: 14, label: 'FCA' },
  { id: 15, label: 'FCB' },
  { id: 16, label: 'FCC' },
  { id: 18, label: 'M' },
];

// Conceptos de percepción (COPE_ID_COPE)
const PERCEPCION_OPCIONES = [
  { id: 1, label: 'Ganancias' },
  { id: 2, label: 'IVA' },
  { id: 3, label: 'IIBB Bs.As.' },
  { id: 4, label: 'IIBB CABA' },
  { id: 11, label: 'IIBB CABA AG' },
  { id: 13, label: 'IIBB Córdoba' },
  { id: 10, label: 'Otros' },
];

const ALICUOTAS_IVA = [0, 2.5, 5, 10.5, 21, 27];

// Comprobantes letra C (Factura C / FCE C): los monotributistas no discriminan
// IVA. YiQi exige alícuota "No gravado" en los ítems; el backend la fuerza,
// y acá se refleja en la UI (columna IVA % bloqueada en "No gravado").
const esFacturaC = (f) => [3, 16].includes(Number(f?.tifa_id)) || ['C', 'FCC'].includes((f?.letra || '').toUpperCase());

// Tolerancia de redondeo (pesos) para los chequeos de cuadre.
const TOL = 1.0;

// Valida que los montos de la factura cierren entre sí. Devuelve un array de
// alertas (texto). Si está vacío, la factura cuadra.
const validarMontos = (f) => {
  const alertas = [];
  const num = (x) => (x === '' || x == null ? null : Number(x));

  const esC = esFacturaC(f);
  const netoItems = (f.items || []).reduce((a, it) => a + (Number(it.importe) || 0), 0);
  // Factura C: no discrimina IVA, los ítems van siempre como "No gravado" (0).
  const ivaItems = esC ? 0 : (f.items || []).reduce(
    (a, it) => a + (Number(it.importe) || 0) * (Number(it.alicuota_iva) || 0) / 100, 0);
  const percep = (f.percepciones || []).reduce((a, p) => a + (Number(p.importe) || 0), 0);

  const neto = num(f.neto);
  const iva = num(f.iva);
  const total = num(f.total);

  if (!f.items?.length) alertas.push('La factura no tiene ítems.');
  if (neto != null && Math.abs(netoItems - neto) > TOL)
    alertas.push(`Suma de netos de ítems (${fmtMoney(netoItems)}) ≠ neto gravado (${fmtMoney(neto)}).`);
  if (iva != null && Math.abs(ivaItems - iva) > TOL)
    alertas.push(`IVA calculado de ítems (${fmtMoney(ivaItems)}) ≠ IVA del comprobante (${fmtMoney(iva)}).`);
  const totalCalc = netoItems + ivaItems + percep;
  if (total != null && Math.abs(totalCalc - total) > TOL)
    alertas.push(`Total calculado (${fmtMoney(totalCalc)}) ≠ total del comprobante (${fmtMoney(total)}).`);
  (f.percepciones || []).forEach((p, i) => {
    if (!(Number(p.importe) > 0)) alertas.push(`Percepción #${i + 1} sin importe válido.`);
  });

  return alertas;
};

const FacturasServiciosPage = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [analizando, setAnalizando] = useState(false);
  const [facturas, setFacturas] = useState([]);        // facturas editables
  const [estados, setEstados] = useState({});          // idx -> 'idle'|'loading'|'ok'|'error'
  const [resultados, setResultados] = useState({});    // idx -> { factura_id, error }
  const [expandidos, setExpandidos] = useState({});    // idx -> bool
  const [errores, setErrores] = useState([]);          // errores de análisis
  const [error, setError] = useState(null);
  // buscador manual de proveedor por tarjeta
  const [provQuery, setProvQuery] = useState({});      // idx -> texto
  const [provResults, setProvResults] = useState({});  // idx -> [...]
  const [provBuscando, setProvBuscando] = useState({});// idx -> bool
  const [provEdit, setProvEdit] = useState({});        // idx -> bool (mostrar buscador)

  const reset = () => {
    setFacturas([]); setEstados({}); setResultados({}); setExpandidos({}); setErrores([]);
  };

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const pdfs = list.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length !== list.length) toast.error('Se ignoraron archivos que no son PDF.');
    setFiles(pdfs);
    reset();
    setError(null);
  };

  const analizar = useCallback(async () => {
    if (!files.length) { toast.info('Seleccioná uno o más PDF.'); return; }
    setAnalizando(true); setError(null); reset();
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('archivos', f));
      const res = await fetch(`${API_URL}/api/admin/facturas-servicios/analizar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);

      const fx = (data.facturas || []).map((f) => ({
        ...f,
        clie_id_prov: f.clie_id_prov || (f.candidatos?.[0]?.clie_id_prov ?? ''),
      }));
      setFacturas(fx);
      setErrores(data.errores || []);
      const est = {};
      fx.forEach((_, i) => { est[i] = 'idle'; });
      setEstados(est);

      if (!fx.length) toast.info('No se extrajo ninguna factura.');
      else toast.success(`${fx.length} factura(s) extraída(s) de ${data.total_archivos} archivo(s).`);
      if ((data.errores || []).length) toast.warning(`${data.errores.length} archivo(s) con error.`);
    } catch (err) {
      setError(err.message);
      toast.error('Error al analizar: ' + err.message);
    } finally {
      setAnalizando(false);
    }
  }, [API_URL, token, files]);

  // ── edición de campos ──
  const setCampo = (idx, campo, valor) =>
    setFacturas((p) => p.map((f, i) => (i === idx ? { ...f, [campo]: valor } : f)));

  const setItem = (idx, j, campo, valor) =>
    setFacturas((p) => p.map((f, i) => {
      if (i !== idx) return f;
      const items = f.items.map((it, k) => (k === j ? { ...it, [campo]: valor } : it));
      return { ...f, items };
    }));

  const addItem = (idx) =>
    setFacturas((p) => p.map((f, i) => i === idx
      ? { ...f, items: [...f.items, { descripcion: '', cantidad: 1, precio_unitario: 0, importe: 0, alicuota_iva: esFacturaC(f) ? 0 : 21 }] }
      : f));

  const delItem = (idx, j) =>
    setFacturas((p) => p.map((f, i) => i === idx ? { ...f, items: f.items.filter((_, k) => k !== j) } : f));

  const setPerc = (idx, j, campo, valor) =>
    setFacturas((p) => p.map((f, i) => {
      if (i !== idx) return f;
      const percepciones = f.percepciones.map((pc, k) => (k === j ? { ...pc, [campo]: valor } : pc));
      return { ...f, percepciones };
    }));

  const addPerc = (idx) =>
    setFacturas((p) => p.map((f, i) => i === idx
      ? { ...f, percepciones: [...f.percepciones, { cope_id_cope: 2, descripcion: '', alicuota: null, importe: 0 }] }
      : f));

  const delPerc = (idx, j) =>
    setFacturas((p) => p.map((f, i) => i === idx ? { ...f, percepciones: f.percepciones.filter((_, k) => k !== j) } : f));

  const toggleExpand = (idx) => setExpandidos((p) => ({ ...p, [idx]: !p[idx] }));

  // ── buscador manual de proveedor ──
  const buscarProv = async (idx) => {
    const f = facturas[idx];
    const q = (provQuery[idx] !== undefined ? provQuery[idx] : (f.cuit_emisor || '')).trim();
    if (q.length < 2) { toast.info('Escribí al menos 2 caracteres (CUIT o nombre).'); return; }
    setProvBuscando((p) => ({ ...p, [idx]: true }));
    try {
      const res = await fetch(
        `${API_URL}/api/admin/facturas-servicios/buscar-proveedor?q=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      setProvResults((p) => ({ ...p, [idx]: data.resultados || [] }));
      if (!(data.resultados || []).length) toast.info('Sin resultados para esa búsqueda.');
    } catch (e) {
      toast.error('Error al buscar proveedor: ' + e.message);
    } finally {
      setProvBuscando((p) => ({ ...p, [idx]: false }));
    }
  };

  const elegirProv = (idx, c) => {
    setFacturas((p) => p.map((f, i) => (i === idx
      ? { ...f, clie_id_prov: c.clie_id_prov, candidatos: [c], estado_proveedor: 'auto' }
      : f)));
    setProvEdit((p) => ({ ...p, [idx]: false }));
    setProvResults((p) => ({ ...p, [idx]: [] }));
  };

  // ── validación + creación ──
  const validar = (f) => {
    if (!f.clie_id_prov) return 'Falta el proveedor.';
    if (!f.tifa_id) return 'Falta el tipo de comprobante.';
    if (!f.punto_de_venta) return 'Falta el punto de venta.';
    if (!f.numero) return 'Falta el número.';
    if (!f.fecha_emision) return 'Falta la fecha de emisión.';
    if (!f.items?.length) return 'La factura no tiene ítems.';
    return null;
  };

  const crearFactura = async (idx) => {
    const f = facturas[idx];
    const err = validar(f);
    if (err) { toast.error(err); return; }
    setEstados((p) => ({ ...p, [idx]: 'loading' }));
    try {
      const payload = {
        facturas: [{
          archivo: f.archivo,
          clie_id_prov: Number(f.clie_id_prov),
          razon_social_pdf: f.razon_social_pdf,
          cuit_emisor: f.cuit_emisor,
          tifa_id: Number(f.tifa_id),
          punto_de_venta: Number(f.punto_de_venta),
          numero: Number(f.numero),
          fecha_emision: f.fecha_emision,
          fecha_1v: f.fecha_1v || null,
          items: (f.items || []).map((it) => ({
            descripcion: it.descripcion || '',
            cantidad: Number(it.cantidad) || 1,
            precio_unitario: it.precio_unitario === '' || it.precio_unitario == null ? null : Number(it.precio_unitario),
            importe: Number(it.importe) || 0,
            // Factura C: siempre "No gravado" (el backend asigna ALIV 6).
            alicuota_iva: esFacturaC(f) ? 0 : (Number(it.alicuota_iva) || 0),
          })),
          percepciones: (f.percepciones || []).map((pc) => ({
            cope_id_cope: Number(pc.cope_id_cope),
            importe: Number(pc.importe) || 0,
            alicuota: pc.alicuota === '' || pc.alicuota == null ? null : Number(pc.alicuota),
            descripcion: pc.descripcion || '',
          })),
        }],
      };
      const res = await fetch(`${API_URL}/api/admin/facturas-servicios/confirmar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      const r = (data.resultados || [])[0] || {};
      if (!r.ok) throw new Error(r.error || 'YiQi rechazó la factura');

      setEstados((p) => ({ ...p, [idx]: 'ok' }));
      setResultados((p) => ({ ...p, [idx]: { factura_id: r.factura_id, comprobante: r.comprobante } }));
      toast.success(`Factura #${r.factura_id} creada (${r.comprobante})`);
    } catch (err2) {
      setEstados((p) => ({ ...p, [idx]: 'error' }));
      setResultados((p) => ({ ...p, [idx]: { error: err2.message } }));
      toast.error(`Error en ${f.razon_social_pdf || f.archivo}: ${err2.message}`);
    }
  };

  const crearTodas = async () => {
    const idxs = facturas.map((_, i) => i).filter((i) => estados[i] === 'idle' && !validar(facturas[i]));
    if (!idxs.length) { toast.info('No hay facturas listas (revisá proveedor y campos obligatorios).'); return; }
    for (const i of idxs) {
      // eslint-disable-next-line no-await-in-loop
      await crearFactura(i);
    }
  };

  const totalGeneral = facturas.reduce((acc, f) => acc + (f.total || 0), 0);
  const creadas = Object.values(estados).filter((e) => e === 'ok').length;

  return (
    <div className="text-gray-100">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#00a1df] to-blue-400 bg-clip-text text-transparent flex items-center gap-3">
              <FileSpreadsheet className="w-7 h-7 text-[#00a1df]" />
              Facturas de Servicios
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Subí uno o varios PDF de facturas de servicios y generá las facturas de compra por proveedor
            </p>
          </div>
        </div>

        {/* Carga */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex-1 flex items-center gap-3 px-4 py-3 bg-gray-900/60 hover:bg-gray-900 border border-dashed border-gray-600 rounded-lg transition-colors text-left"
            >
              <Upload className="w-5 h-5 text-[#00a1df] shrink-0" />
              <span className="min-w-0">
                {files.length
                  ? <span className="font-semibold text-white truncate block">{files.length} archivo(s) seleccionado(s)</span>
                  : <span className="text-gray-400">Seleccionar archivos PDF… (podés elegir varios)</span>}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={onPickFiles}
              className="hidden"
            />
            <button
              onClick={analizar}
              disabled={!files.length || analizando}
              className="flex items-center justify-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95"
            >
              {analizando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {analizando ? 'ANALIZANDO…' : 'ANALIZAR PDFs'}
            </button>
          </div>
          {files.length > 0 && (
            <ul className="mt-3 text-[11px] text-gray-500 space-y-0.5 max-h-24 overflow-y-auto">
              {files.map((f, i) => <li key={i} className="truncate">• {f.name}</li>)}
            </ul>
          )}
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-5 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
            <p className="text-red-300/90 text-sm">{error}</p>
          </div>
        )}

        {errores.length > 0 && (
          <div className="bg-amber-900/20 border border-amber-500/40 rounded-xl p-4 space-y-1">
            <p className="text-amber-300 text-sm font-bold flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Archivos con error</p>
            {errores.map((e, i) => (
              <p key={i} className="text-[11px] text-amber-200/80">• {e.archivo}: {e.error}</p>
            ))}
          </div>
        )}

        {facturas.length > 0 && (
          <>
            {/* Stats + acciones */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="grid grid-cols-3 gap-4 flex-1">
                <StatCard label="Facturas" value={facturas.length} icon={<FileText className="w-5 h-5" />} color="text-[#00a1df]" bg="bg-[#00a1df]/10" />
                <StatCard label="Total" value={fmtMoney(totalGeneral)} icon={<Hash className="w-5 h-5" />} color="text-green-400" bg="bg-green-500/10" />
                <StatCard label="Creadas" value={creadas} icon={<CheckCircle2 className="w-5 h-5" />} color="text-emerald-400" bg="bg-emerald-500/10" />
              </div>
              <button
                onClick={crearTodas}
                className="flex items-center justify-center gap-2 bg-[#00a1df] hover:bg-[#0090c7] text-white px-5 py-2.5 rounded-lg shadow-lg transition-all font-black text-xs active:scale-95 whitespace-nowrap"
              >
                <Play className="w-4 h-4 fill-current" />
                CREAR TODAS
              </button>
            </div>

            {/* Tarjetas */}
            <div className="space-y-4">
              {facturas.map((f, idx) => {
                const estado = estados[idx] || 'idle';
                const resultado = resultados[idx];
                const isExpanded = expandidos[idx] ?? true;
                const candidatos = f.candidatos || [];
                const noEncontrado = f.estado_proveedor === 'no_encontrado';
                const alertas = validarMontos(f);

                return (
                  <div key={idx} className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
                    {/* encabezado de la tarjeta */}
                    <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-700/60 bg-gray-800/60">
                      <div className="min-w-0">
                        <p className="font-bold text-white truncate flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#00a1df] shrink-0" />
                          {f.razon_social_pdf || '(sin nombre)'}
                          <span className="text-[10px] font-mono text-gray-500">{f.letra ? `· ${f.letra}` : ''}</span>
                          {alertas.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5">
                              <AlertTriangle className="w-3 h-3" /> No cuadra ({alertas.length})
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-gray-500 font-mono truncate">
                          {f.archivo} · CUIT {f.cuit_emisor || '—'} · {fmtMoney(f.total)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <EstadoBadge estado={estado} resultado={resultado} />
                        <button
                          onClick={() => crearFactura(idx)}
                          disabled={estado === 'loading' || estado === 'ok' || !!validar(f)}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all active:scale-95 ${
                            estado === 'ok'
                              ? 'bg-green-900/30 text-green-400 border border-green-500/20 cursor-default'
                              : estado === 'loading'
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : validar(f)
                              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                              : estado === 'error'
                              ? 'bg-red-600 hover:bg-red-500 text-white'
                              : 'bg-[#00a1df] hover:bg-[#0090c7] text-white shadow-lg shadow-[#00a1df]/20'
                          }`}
                        >
                          {estado === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : estado === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" />
                            : <Receipt className="w-3.5 h-3.5" />}
                          {estado === 'ok' ? 'Creada' : estado === 'error' ? 'Reintentar' : 'Crear factura'}
                        </button>
                        <button onClick={() => toggleExpand(idx)} className="p-2 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-700/40">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-5 space-y-5">
                        {/* alertas de cuadre */}
                        {alertas.length > 0 && (
                          <div className="bg-amber-900/20 border border-amber-500/40 rounded-lg px-4 py-3">
                            <p className="text-amber-300 text-xs font-bold flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4" /> Revisar — los montos no cierran
                            </p>
                            <ul className="text-[11px] text-amber-200/90 space-y-0.5 list-disc list-inside">
                              {alertas.map((a, k) => <li key={k}>{a}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* cabecera editable */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {/* Proveedor */}
                          <Campo label="Proveedor (YiQi)">
                            {candidatos.length > 1 && !provEdit[idx] ? (
                              <select value={f.clie_id_prov || ''} onChange={(e) => setCampo(idx, 'clie_id_prov', e.target.value)} className={inputCls}>
                                <option value="">— Elegir —</option>
                                {candidatos.map((c) => <option key={c.clie_id_prov} value={c.clie_id_prov}>{c.razon_social} (ID {c.clie_id_prov})</option>)}
                              </select>
                            ) : f.clie_id_prov && !provEdit[idx] ? (
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs text-gray-300 truncate flex items-center gap-1.5">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500/70 shrink-0" />
                                  {candidatos[0]?.razon_social || '—'} <span className="text-gray-600">· ID {f.clie_id_prov}</span>
                                </span>
                                <button onClick={() => setProvEdit((p) => ({ ...p, [idx]: true }))} className="text-[10px] text-[#00a1df] hover:underline shrink-0">cambiar</button>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                {noEncontrado && !f.clie_id_prov && (
                                  <span className="flex items-center gap-1 text-[10px] text-amber-400"><UserX className="w-3 h-3" /> No se encontró por CUIT — buscalo abajo</span>
                                )}
                                <div className="flex gap-1.5">
                                  <input
                                    value={provQuery[idx] !== undefined ? provQuery[idx] : (f.cuit_emisor || '')}
                                    onChange={(e) => setProvQuery((p) => ({ ...p, [idx]: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') buscarProv(idx); }}
                                    placeholder="CUIT o nombre…"
                                    className={inputCls}
                                  />
                                  <button onClick={() => buscarProv(idx)} disabled={provBuscando[idx]} className="px-2.5 py-1.5 rounded-md bg-[#00a1df] hover:bg-[#0090c7] disabled:bg-gray-700 text-white text-[11px] font-bold shrink-0">
                                    {provBuscando[idx] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Buscar'}
                                  </button>
                                </div>
                                {(provResults[idx] || []).length > 0 && (
                                  <div className="max-h-40 overflow-y-auto rounded-md border border-gray-700 divide-y divide-gray-800 bg-gray-900/60">
                                    {provResults[idx].map((c) => (
                                      <button key={c.clie_id_prov} onClick={() => elegirProv(idx, c)} className="w-full text-left px-2 py-1.5 hover:bg-gray-700/40 text-[11px]">
                                        <span className="text-gray-200">{c.razon_social}</span>
                                        <span className="text-gray-500 font-mono"> · {c.cuit} · ID {c.clie_id_prov}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </Campo>

                          <Campo label="Tipo">
                            <select value={f.tifa_id || ''} onChange={(e) => setCampo(idx, 'tifa_id', e.target.value)} className={inputCls}>
                              <option value="">—</option>
                              {TIFA_OPCIONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                            </select>
                          </Campo>

                          <Campo label="Punto de venta">
                            <input type="number" value={f.punto_de_venta || ''} onChange={(e) => setCampo(idx, 'punto_de_venta', e.target.value)} className={inputCls} />
                          </Campo>

                          <Campo label="Número">
                            <input type="number" value={f.numero || ''} onChange={(e) => setCampo(idx, 'numero', e.target.value)} className={inputCls} />
                          </Campo>

                          <Campo label="Fecha de emisión">
                            <input type="date" value={f.fecha_emision || ''} onChange={(e) => setCampo(idx, 'fecha_emision', e.target.value)} className={inputCls} />
                          </Campo>

                          <Campo label="1er. venc.">
                            <input type="date" value={f.fecha_1v || ''} onChange={(e) => setCampo(idx, 'fecha_1v', e.target.value)} className={inputCls} />
                          </Campo>

                          <Campo label="Neto / IVA / Total">
                            <span className="text-xs text-gray-400 font-mono">{fmtMoney(f.neto)} · {fmtMoney(f.iva)} · <span className="text-white font-bold">{fmtMoney(f.total)}</span></span>
                          </Campo>
                        </div>

                        {/* Ítems */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Ítems</h4>
                            <button onClick={() => addItem(idx)} className="flex items-center gap-1 text-[11px] text-[#00a1df] hover:text-[#4fc3f7]"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                          </div>
                          <div className="overflow-x-auto rounded-lg border border-gray-700/60">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-900/50 text-gray-500 uppercase tracking-wider">
                                <tr>
                                  <th className="text-left py-2 px-2 font-bold">Descripción</th>
                                  <th className="text-right py-2 px-2 font-bold w-16">Cant.</th>
                                  <th className="text-right py-2 px-2 font-bold w-28">P. unit.</th>
                                  <th className="text-right py-2 px-2 font-bold w-28">Neto</th>
                                  <th className="text-right py-2 px-2 font-bold w-20">IVA %</th>
                                  <th className="w-8"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800">
                                {f.items.map((it, j) => (
                                  <tr key={j}>
                                    <td className="px-2 py-1"><input value={it.descripcion} onChange={(e) => setItem(idx, j, 'descripcion', e.target.value)} className={cellCls} /></td>
                                    <td className="px-2 py-1"><input type="number" value={it.cantidad} onChange={(e) => setItem(idx, j, 'cantidad', e.target.value)} className={cellCls + ' text-right'} /></td>
                                    <td className="px-2 py-1"><input type="number" value={it.precio_unitario ?? ''} onChange={(e) => setItem(idx, j, 'precio_unitario', e.target.value)} className={cellCls + ' text-right'} /></td>
                                    <td className="px-2 py-1"><input type="number" value={it.importe} onChange={(e) => setItem(idx, j, 'importe', e.target.value)} className={cellCls + ' text-right'} /></td>
                                    <td className="px-2 py-1">
                                      {esFacturaC(f) ? (
                                        <span className="block text-right text-[11px] text-gray-400 italic whitespace-nowrap" title="Factura C: los ítems van con alícuota 'No gravado'">No gravado</span>
                                      ) : (
                                        <select value={it.alicuota_iva} onChange={(e) => setItem(idx, j, 'alicuota_iva', e.target.value)} className={cellCls + ' text-right'}>
                                          {ALICUOTAS_IVA.map((a) => <option key={a} value={a}>{a}</option>)}
                                        </select>
                                      )}
                                    </td>
                                    <td className="px-1 py-1 text-center">
                                      <button onClick={() => delItem(idx, j)} className="text-gray-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </td>
                                  </tr>
                                ))}
                                {!f.items.length && <tr><td colSpan={6} className="text-center text-gray-600 py-3 text-[11px]">Sin ítems</td></tr>}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Percepciones */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Percepciones</h4>
                            <button onClick={() => addPerc(idx)} className="flex items-center gap-1 text-[11px] text-[#00a1df] hover:text-[#4fc3f7]"><Plus className="w-3.5 h-3.5" /> Agregar</button>
                          </div>
                          {f.percepciones.length ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-700/60">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-900/50 text-gray-500 uppercase tracking-wider">
                                  <tr>
                                    <th className="text-left py-2 px-2 font-bold w-44">Tipo</th>
                                    <th className="text-left py-2 px-2 font-bold">Descripción</th>
                                    <th className="text-right py-2 px-2 font-bold w-24">Alíc. %</th>
                                    <th className="text-right py-2 px-2 font-bold w-28">Importe</th>
                                    <th className="w-8"></th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                  {f.percepciones.map((pc, j) => (
                                    <tr key={j}>
                                      <td className="px-2 py-1">
                                        <select value={pc.cope_id_cope} onChange={(e) => setPerc(idx, j, 'cope_id_cope', e.target.value)} className={cellCls}>
                                          {PERCEPCION_OPCIONES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                        </select>
                                      </td>
                                      <td className="px-2 py-1"><input value={pc.descripcion || ''} onChange={(e) => setPerc(idx, j, 'descripcion', e.target.value)} className={cellCls} /></td>
                                      <td className="px-2 py-1"><input type="number" value={pc.alicuota ?? ''} onChange={(e) => setPerc(idx, j, 'alicuota', e.target.value)} className={cellCls + ' text-right'} /></td>
                                      <td className="px-2 py-1"><input type="number" value={pc.importe} onChange={(e) => setPerc(idx, j, 'importe', e.target.value)} className={cellCls + ' text-right'} /></td>
                                      <td className="px-1 py-1 text-center"><button onClick={() => delPerc(idx, j)} className="text-gray-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-[11px] text-gray-600 italic">Sin percepciones</p>
                          )}
                        </div>

                        {estado === 'error' && resultado?.error && (
                          <div className="bg-red-900/20 border border-red-500/40 rounded-lg px-4 py-2 text-[11px] text-red-300 flex items-center gap-2">
                            <XCircle className="w-4 h-4 shrink-0" /> {resultado.error}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// helpers visuales
const inputCls = "w-full bg-gray-900 border border-gray-600 rounded-md px-2 py-1.5 text-xs text-white focus:border-[#00a1df] focus:outline-none";
const cellCls = "w-full bg-gray-900/60 border border-gray-700 rounded px-2 py-1 text-xs text-white focus:border-[#00a1df] focus:outline-none";

const Campo = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</label>
    {children}
  </div>
);

const EstadoBadge = ({ estado, resultado }) => {
  if (estado === 'ok' && resultado) {
    return (
      <span className="flex flex-col items-end">
        <span className="flex items-center gap-1 text-xs text-green-400 font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> Factura #{resultado.factura_id}</span>
        {resultado.comprobante && <span className="text-[10px] text-gray-500 font-mono">{resultado.comprobante}</span>}
      </span>
    );
  }
  if (estado === 'loading') return <span className="flex items-center gap-1.5 text-xs text-[#00a1df]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creando...</span>;
  if (estado === 'error') return <span className="flex items-center gap-1 text-xs text-red-400 font-bold"><XCircle className="w-3.5 h-3.5" /> Error</span>;
  return <span className="text-xs text-gray-600 italic">pendiente</span>;
};

const StatCard = ({ label, value, icon, color, bg }) => (
  <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-3 shadow-lg">
    <div className={`p-2 rounded-lg ${bg} ${color}`}>{icon}</div>
    <div className="min-w-0">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{label}</p>
      <p className="text-lg font-black text-white truncate">{value}</p>
    </div>
  </div>
);

export default FacturasServiciosPage;
